import React from 'react';
import _ from 'lodash';
import { Padding } from 'components/base/layout-components';
import { Actions, Header, Scopes } from './sections';
import { handleSave } from './createEditProjectModal.service';
import useCreateEditProjectModal from './useCreateEditProjectModal';
import { isEmptyObjectAndKeys } from './utils';
import IterationsSettings from './IterationsSettings';
import { CircularProgress } from '@material-ui/core';
import withPermissions from 'connected-components/HOC/PermissionsHOC';
import { ACCOUNT_TYPES_MAP, FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import { useSelector } from 'react-redux';
import { pmIntegrationSelector } from '../../state/selectors/sensors-authorizations';
import { PM_PROVIDERS } from '../../common/constants/constants';
import useSettingsService from 'components/hooks/useSettingsService';
import { boardsProviderKeysSelector } from 'state/selectors/boards';
import { FILTER_TYPES } from 'common/constants/filters';
import NameSelector from './NameSelector';
import CadenceSelector from './CadenceSelector';
import BoardsSelector from './BoardsSelector';
import { Stack } from '@mui/material';
import { Body2 } from '@linearb/linui-common/typography';
import {
  StyledCreateEditProjectModal,
  StyledScroll
} from './CreateEditProjectModal.style';
import useAllCustomFieldsMapper from 'views/root/views/home/hooks/useAllCustomFieldsMapper';
import useEntitiesLimitation from 'views/root/views/home/hooks/useEntitiesLimitation';
import AreasSelector from './AreasSelector';

const CreateEditProjectModal = ({
  projectName = '',
  projectId = '',
  isEdit = true,
  onSaveClick = _.noop,
  pmSensorAuthId
}) => {
  const pmSensor = useSelector(pmIntegrationSelector);
  const { sensor_id: sensorId } = pmSensor;
  const allBoardsByProviderIds = useSelector(boardsProviderKeysSelector);

  const { entitiesDeviation, entitiesLimit } = useEntitiesLimitation(
    FILTER_TYPES.BOARD
  );

  const {
    selectedFilters,
    setSelectedFilters,
    selectedMethod,
    setSelectedMethod,
    curProjectName,
    setCurProjectName,
    nameValidator,
    upsertConfiguration,
    isFormValid,
    isSkipProjectsBoardSelectionFFEnabled,
    includeIterations,
    setIncludeIterations,
    excludeIterations,
    setExcludeIterations,
    loadingConfiguration,
    onDeleteFilter
  } = useCreateEditProjectModal(projectName, projectId, pmSensor);

  const { customFieldsMapper, isLoading: isLoadingCustomFieldsMapper } =
    useAllCustomFieldsMapper({ sensorId });

  const { getSettingOrDefinitionValue, updateSetting } = useSettingsService();
  const projectsIterationsConfigEnabledFFEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.projectsIterationsConfigEnabled
  });

  const onChangeBoards = updatedFilters => {
    const isEmpty = _.isEmpty(updatedFilters[FILTER_TYPES.BOARD]);
    const filtersAfterDelete = {
      ..._.omit(selectedFilters, FILTER_TYPES.BOARD),
      ...(isEmpty ? {} : updatedFilters)
    };

    setSelectedFilters(filtersAfterDelete);
  };
  const isSaveEnabled =
    nameValidator.isValid &&
    isFormValid &&
    !isEmptyObjectAndKeys(selectedFilters);

  const getChildren = ({ includeAdvanced = false } = {}) => {
    if (loadingConfiguration || isLoadingCustomFieldsMapper) {
      return (
        <Stack alignItems='center' justifyContent='center' height='100%'>
          <CircularProgress />
        </Stack>
      );
    }

    return (
      <>
        <StyledCreateEditProjectModal>
          <Stack>
            <Header isEdit={isEdit} projectName={projectName} />
            <StyledScroll>
              <NameSelector
                curProjectName={curProjectName}
                projectName={projectName}
                setCurProjectName={setCurProjectName}
              />
              {pmSensor.sensor_id === PM_PROVIDERS.JIRA && (
                <BoardsSelector
                  selectedBoards={_.pick(selectedFilters, FILTER_TYPES.BOARD)}
                  onChange={onChangeBoards}
                  customFieldsMapper={customFieldsMapper}
                />
              )}
              {pmSensor?.sensor_id === PM_PROVIDERS.AZURE_BOARDS && (
                <AreasSelector
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                />
              )}
              <CadenceSelector
                value={selectedMethod}
                onChange={setSelectedMethod}
              />

              <Scopes
                curProjectName={curProjectName}
                pmSensorAuthId={pmSensorAuthId}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                isEdit={isEdit}
                onDeleteFilter={onDeleteFilter}
                customFieldsMapper={customFieldsMapper}
              />
              {includeAdvanced && projectsIterationsConfigEnabledFFEnabled && (
                <Padding t='medium'>
                  <IterationsSettings
                    includeIterations={includeIterations}
                    setIncludeIterations={setIncludeIterations}
                    excludeIterations={excludeIterations}
                    setExcludeIterations={setExcludeIterations}
                  />
                </Padding>
              )}
            </StyledScroll>
          </Stack>
          <footer>
            <Body2>
              {!isSaveEnabled
                ? 'Please fill in all the required fields to continue'
                : ''}
            </Body2>

            <Actions
              handleSave={async () =>
                await handleSave({
                  selectedMethod,
                  selectedFilters,
                  onSaveClick,
                  projectName,
                  curProjectName,
                  upsertConfiguration,
                  nameValidator,
                  isFormValid,
                  isSkipProjectsBoardSelectionFFEnabled,
                  includeIterations,
                  excludeIterations,
                  pmSensor,
                  allBoardsByProviderIds,
                  getSettingOrDefinitionValue,
                  updateSetting,
                  entitiesDeviation,
                  entitiesLimit
                })
              }
              isValid={isSaveEnabled}
              selectedFilters={selectedFilters}
            />
          </footer>
        </StyledCreateEditProjectModal>
      </>
    );
  };

  return withPermissions({
    WrappedComponent: getChildren({ includeAdvanced: true }),
    requiredPermissions: [
      ACCOUNT_TYPES_MAP.SuperUser,
      ACCOUNT_TYPES_MAP.Primary
    ],
    DefaultOnHideComponent: getChildren()
  });
};
export default CreateEditProjectModal;
